import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Shield, Zap, Menu, X, Crown } from 'lucide-react';
import { useState } from 'react';

const FOUNDER_EMAIL = 'info@defences.ai';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isFounder = (user?.email || '').toLowerCase() === FOUNDER_EMAIL;

  const navLinks = [
    { path: '/dashboard', label: 'DASHBOARD', auth: true },
    { path: '/marketplace', label: 'AGENTS', auth: false },
    { path: '/pricing', label: 'PRICING', auth: false },
    { path: '/settings', label: 'SETTINGS', auth: true },
    { path: '/admin', label: 'ADMIN', auth: true, admin: true },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#020202]/90 backdrop-blur-xl border-b border-white/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <Shield className="w-5 h-5 text-[#FF7A3D]" />
          <span className="font-heading font-bold text-sm text-white tracking-tight">DEFENCES<span className="text-[#FF7A3D]">.AI</span></span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.filter(l => (!l.auth || user) && (!l.admin || user?.is_admin)).map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`font-mono text-xs transition-colors ${link.admin ? (isActive(link.path) ? 'text-[#ff003c]' : 'text-[#ff003c]/50 hover:text-[#ff003c]') : (isActive(link.path) ? 'text-[#FF7A3D]' : 'text-[#999] hover:text-[#FF7A3D]')}`}
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
          {isFounder && (
            <Link
              to="/founder"
              className={`flex items-center gap-1.5 font-mono text-xs transition-colors ${isActive('/founder') ? 'text-[#FF7A3D]' : 'text-[#FF7A3D]/70 hover:text-[#FF7A3D]'}`}
              data-testid="nav-founder-cockpit"
            >
              <Crown className="w-3.5 h-3.5" /> COCKPIT
            </Link>
          )}
          {user ? (
            <div className="flex items-center gap-3">
              <Badge className="bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20 font-mono text-[10px]">
                <Zap className="w-3 h-3 mr-1" /> {(user.credits || 0).toFixed(1)}
              </Badge>
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="font-mono text-xs text-[#aaa] hover:text-[#ff003c] transition-colors"
                data-testid="nav-logout"
              >
                EXIT
              </button>
            </div>
          ) : (
            <Button
              onClick={() => navigate('/auth')}
              className="bg-[#FF7A3D] text-black font-heading font-bold uppercase tracking-widest hover:bg-[#FF7A3D]/90 rounded-sm text-[10px] h-8 px-4"
              data-testid="nav-access-btn"
            >
              ACCESS
            </Button>
          )}
        </div>

        {/* Mobile toggle */}
        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-white" data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0a0a0a] border-b border-white/5 px-6 py-4 space-y-3" data-testid="mobile-menu">
          {navLinks.filter(l => (!l.auth || user) && (!l.admin || user?.is_admin)).map(link => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className={`block font-mono text-sm ${isActive(link.path) ? 'text-[#FF7A3D]' : 'text-[#888]'}`}
            >
              {link.label}
            </Link>
          ))}
          {isFounder && (
            <Link
              to="/founder"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-1.5 font-mono text-sm text-[#FF7A3D]"
              data-testid="nav-mobile-founder-cockpit"
            >
              <Crown className="w-4 h-4" /> COCKPIT
            </Link>
          )}
          {user ? (
            <button
              onClick={() => { logout(); navigate('/'); setMobileOpen(false); }}
              className="block font-mono text-sm text-[#ff003c]"
            >
              TERMINATE
            </button>
          ) : (
            <button
              onClick={() => { navigate('/auth'); setMobileOpen(false); }}
              className="block font-mono text-sm text-[#FF7A3D]"
            >
              ACCESS
            </button>
          )}
        </div>
      )}
    </nav>
  );
}
